<template>
   <div class="chart-gallery-details-container">
      <!-- CHART DESCRIPTION-->
      <ChartDesc
         v-if="playgroundData && playgroundData.header"
         :chart-title="playgroundData.header"
         :chart-desc="playgroundData.longDescription"
         :framework="currentFramework"
         :framework-name="frameworkName"
         :isframeworkvisible="loadAllProps"
         :product="playgroundData.product"
         :get-started-ctas="(playgroundData.getStarted && playgroundData.getStarted.ctas) || []"
      />

      <!-- CHART VIEW -->
      <h2 v-if="playgroundData.shortDescription">
         {{ descriptionJoiner(playgroundData.shortDescription) }}
      </h2>
      <div class="show-chart">
         <ChartView
            :html-code="playgroundData.code.htmlCode"
            :js-code="playgroundData.code.jsCode"
            :css-code="playgroundData.code.cssCode"
            :seo-img-url="playgroundData.preview"
            :seo-img-alt="playgroundData.header"
            :exclude-themes="['fusion']"
            @themechanged="selectTheme"
         />
         <a v-if="prevLink" :href="prevLink" class="prev-slide-btn" @click.prevent="goPrev()">
            <i class="fc_keyboard_arrow_left" />
         </a>
         <a v-if="nextLink" :href="nextLink" class="next-slide-btn" @click.prevent="goNext()">
            <i class="fc_keyboard_arrow_right" />
         </a>
      </div>

      <div v-if="isClient" class="row">
         <mq-layout class="code-view-wrapper col-12" :mq="['xs', 'sm', 'md', 'navigation']">
            <CodeViewSm
               :display="playgroundData.code.display"
               :fiddle="playgroundData.fiddle"
               :slug="playgroundData.slug"
               :is-static="playgroundData.isStatic"
               :selected-theme="selectedTheme"
               :code="playgroundData.code"
               @frameworkSelected="updateTitle"
            />
         </mq-layout>
         <mq-layout class="code-view-wrapper col-12" mq="lg+">
            <CodeView
               :display="playgroundData.code.display"
               :fiddle="playgroundData.fiddle"
               :slug="playgroundData.slug"
               :is-static="playgroundData.isStatic"
               :selected-theme="selectedTheme"
               :code-details="codeViewDetails"
               :code="playgroundData.code"
               @frameworkSelected="updateTitle"
            />
         </mq-layout>
      </div>

      <!-- Related Charts and Widgets -->
      <!-- to adjust height and width of a image 'imgfix' send as a prop -->
      <div
         v-if="playgroundData && playgroundData.relatedChartsWidgets && loadAllProps"
         class="explore-charts-section"
      >
         <ExploreCharts :chart-data="playgroundData.relatedChartsWidgets" img-fix="imgfix" />
      </div>

      <!-- Build 100+ charts and 2000+ maps in React -->
      <!-- to adjust height and width of a image 'imgfix' send as a prop -->
      <div
         v-if="playgroundData && playgroundData.BuildchartsMaps && loadAllProps"
         class="explore-charts-section build-charts-maps"
      >
         <ExploreCharts :chart-data="playgroundData.BuildchartsMaps" img-fix="imgfix" />
      </div>

      <!-- Explore dashboard with Simple Column -->
      <div
         v-if="playgroundData && playgroundData.dashboardSimpleColumn && loadAllProps"
         class="explore-charts-section dashboard-simple-column"
      >
         <ExploreCharts :chart-data="playgroundData.dashboardSimpleColumn" />
      </div>

      <!-- Get Started -->
      <div
         v-if="playgroundData && playgroundData.getStarted && loadAllProps"
         class="get-started-section"
      >
         <GetStarted
            :get-started-content="playgroundData.getStarted"
            :framework-name="currentFramework"
         />
      </div>
   </div>
</template>

<script>
import { mapState } from 'vuex';
import ChartDesc from '../../components/integrations/ChartDesc.vue';
import GetStarted from '../../components/integrations/GetStarted.vue';
import languages from '../../components/playground/languages.js';
import ExploreCharts from '../../components/integrations/ExploreCharts.vue';
import metaGenerator from '../../util/meta.js';
import config from '../../components/config.js';

export default {
   name: 'Details',
   components: {
      ChartDesc,
      ExploreCharts,
      GetStarted,
      ChartView: () => import('../../components/playground/ChartView'),
      CodeView: () => import('../../components/playground/CodeView'),
      CodeViewSm: () => import('../../components/playground/CodeViewSm'),
   },

   data() {
      return {
         languages,
         isClient: false,
         // Chart gallery pages hide ChartView's theme selector (show-themes="false")
         // and lock to the product's default theme - keep this in sync with
         // config.defaultTheme so the code snippet below matches the rendered chart.
         selectedTheme: config.defaultTheme,
         ...(process.browser && { mixpanelchartGalleryTracker: this.$tracker.chartGallery }),
         collapsibleEl: null,
         nextLink: null,
         prevLink: null,
         ...(process.browser && { tracker: this.$tracker.userProfile }),
         frameworkName: '',
         currentFramework: '',
         frameworkList: {
            js: 'Javascript',
            react: 'React',
            angular4: 'Angular 4',
            jquery: 'jQuery',
            vue: 'Vue',
            ember: 'Ember',
            'react-native': 'React Native',
            angular: 'Angular',
            php: 'php',
            rails: 'Rails',
            csharp: 'Csharp',
            vb: 'Vb',
            python: 'Python',
            java: 'java',
         },
      };
   },

   computed: {
      ...mapState(['playgroundData', 'playgroundSidebarData']),

      collapsibleContent() {
         return this.markedRender(this.playgroundData.longDescription);
      },

      codeViewDetails() {
         return {
            title: this.playgroundData.meta.header,
            description: this.playgroundData.meta.description,
         };
      },

      loadAllProps() {
         return this.playgroundData.loadAllProps || false;
      },
   },

   watch: {
      playgroundData() {
         this.tracker.productBrowse({
            Product: this.playgroundData.product,
            chartCategory: this.$route.params.type,
         });

         if (this.playgroundData) {
            this.$store.commit('SET_SIDEBAR_FLAG', {
               data: true,
            });
         } else {
            this.$store.commit('SET_SIDEBAR_FLAG', {
               data: false,
            });
         }
      },
      $route(to, from) {
         this.updateRoute();
      },
   },

   asyncData({ store, route }) {
      if (typeof window !== 'undefined') {
         return Promise.all([
            store.dispatch('PLAYGROUND_PAGE_DATA_FETCH', {
               type: route.params.type,
               name: route.params.name,
               language: store.state.fcUserSelectedFramework,
            }),
         ]);
      }

      return Promise.all([
         store.dispatch('PLAYGROUND_PAGE_DATA_FETCH', {
            type: route.params.type,
            name: route.params.name,
         }),
      ]);
   },

   beforeRouteUpdate(to, from, next) {
      next();
      this.initNavLinks();

      this.$nextTick(() => {
         this.initCollapsibleEl();
         this.restoreReadMoreState();
      });
   },

   created() {
      this.initNavLinks();
      this.queryFramework = this.$route.query.framework;
      // this.currentFramework = queryFramework;

      if (!this.playgroundData) {
         if (typeof window === 'undefined') {
            throw Object.assign(new Error(), {
               code: 404,
               message: 'Chart Details NOT FOUND',
            });
         } else {
            this.$store.commit('SET_SIDEBAR_FLAG', {
               data: false,
            });
         }
      }
   },

   beforeMount() {
      const frameworks = this.languages.map(el => el.value);
      const lang =
         frameworks.indexOf(this.$getUserSelectedFramework()) > -1
            ? this.$getUserSelectedFramework()
            : 'js';
      this.$set(this.$store.state, 'fcUserSelectedFramework', lang);
   },

   mounted() {
      this.isClient = true;
      const localFramework = this.$getUserSelectedFramework();

      // if framework is given as param then set the fw
      if (this.queryFramework) {
         this.$setUserSelectedFramework(this.queryFramework);
         this.currentFramework = this.queryFramework;
      } else if (localFramework) {
         this.currentFramework = localFramework;
      } else {
         this.currentFramework = 'js';
      }

      if (
         !this.currentFramework ||
         this.currentFramework === 'js' ||
         !this.frameworkList[this.currentFramework.toLowerCase()]
      ) {
         this.currentFramework = 'javascript';
      }

      this.frameworkName = this.frameworkList[this.currentFramework];

      this.$nextTick(() => {
         !this.$route.query.framework && this.updateRoute();
      });

      this.initCollapsibleEl();
      this.initButton();
      setTimeout(this.initButton, 1000);
      this.updateTitle();
      this.mixpanelchartGalleryTracker.visited();
   },

   methods: {
      selectTheme(theme) {
         this.selectedTheme = theme;
      },

      updateRoute() {
         if (!this.playgroundData.isStatic) {
            this.$router
               .replace({
                  query: {
                     ...this.$route.query,
                     framework: this.currentFramework || undefined,
                  },
               })
               .catch(err => {});
         }
      },

      // Updating the title with Framework Name
      updateTitle(frameworkName) {
         frameworkName &&
            (this.currentFramework = frameworkName === 'js' ? 'javascript' : frameworkName);
         this.updateRoute();
      },

      readMoreClickListener() {
         if (!this.collapsibleEl) return;
         this.collapsibleEl.classList.add('description-transition');
         this.collapsibleEl.style.maxHeight = '1000px';
         this.isCollapsed = false;
      },
      initCollapsibleEl() {
         this.collapsibleEl = this.$el.querySelector('.description-container-gallery');
      },
      restoreReadMoreState() {
         if (!this.collapsibleEl) return;
         this.collapsibleEl.classList.remove('description-transition');
         this.collapsibleEl.style.maxHeight = '85px';
         this.isCollapsed = true;
         this.initButton();
      },
      initButton() {
         if (!this.collapsibleEl) return;
         if (this.collapsibleEl.scrollHeight < 130) {
            this.isCollapsed = false;
         } else {
            this.isCollapsed = true;
         }
      },
      goNext() {
         this.$bar.start();
         this.$router.push(this.nextLink, this.$bar.finish);
      },
      goPrev() {
         this.$bar.start();
         this.$router.push(this.prevLink, this.$bar.finish);
      },
      initNavLinks() {
         this.nextLink = null;
         this.prevLink = null;

         if (!this.playgroundSidebarData) return;

         const currentPath = this.$route.path;
         let hitItem = false;

         function extractLinks({ items }, parentItems, rootIdx) {
            if (!items) return false;
            if (hitItem) return true;
            let prevLink;
            let nextLink;

            for (let idx = 0; idx < items.length; idx += 1) {
               const subItem = items[idx];
               if (subItem.link === currentPath) {
                  if (idx > 1) {
                     prevLink = items[idx - 1].link;
                  } else if (rootIdx > 0) {
                     const prevList = parentItems[rootIdx - 1].items;
                     if (prevList && prevList.length > 0) {
                        prevLink = prevList[prevList.length - 1].link;
                     }
                  }

                  if (idx < items.length - 1) {
                     nextLink = items[idx + 1].link;
                  } else if (rootIdx < parentItems.length - 1) {
                     const nextList = parentItems[rootIdx + 1].items;
                     if (nextList && nextList.length > 1) {
                        nextLink = nextList[1].link;
                     }
                  }

                  hitItem = true;
                  break;
               }

               if (subItem.items && subItem.items.length > 0) {
                  if (!hitItem) {
                     [prevLink, nextLink] = extractLinks(subItem, items, idx);
                  } else break;
               }
            }

            return [prevLink, nextLink];
         }

         const chartGalleryItems = this.playgroundSidebarData[0].items;
         for (let index = 0; index < chartGalleryItems.length; index += 1) {
            const subItem = chartGalleryItems[index];
            if (!hitItem) {
               [this.prevLink, this.nextLink] = extractLinks(subItem, chartGalleryItems, index);
            } else break;
         }
      },
   },

   metaInfo() {
      return metaGenerator(this.playgroundData);
   },
};
</script>
<style lang="scss" scoped>
@import '../../util';

.chart-gallery-details-container {
   width: 100%;
   overflow: hidden;
   background-color: #f9f9fb;
   padding: 45px 45px 75px;
   @media (max-width: 991px) {
      padding: 30px 30px 101px;
      margin-top: 0;
   }
   @media (max-width: 540px) {
      padding: 30px 15px 101px;
      margin-top: 0;
   }

   .show-chart {
      margin-top: 45px;
      position: relative;

      .next-slide-btn,
      .prev-slide-btn {
         position: absolute;
         top: 40%;
         display: flex;
         padding: 10px;
         background-color: #fff;
         color: #000;
         box-shadow: 0 4px 9px 0 rgba(23, 28, 33, 0.12);
         border-radius: 50%;
         transform: scale(0.9);
         text-decoration: none;

         &:hover {
            text-decoration: none;
         }

         @include media-breakpoint-up(sm) {
            transform: scale(1);
         }

         i {
            position: relative;
            top: 1px;
         }
      }
      .next-slide-btn {
         right: -11px;

         @include media-breakpoint-up(sm) {
            right: -22.5px;
         }
      }

      .prev-slide-btn {
         left: -11px;

         @include media-breakpoint-up(sm) {
            left: -22.5px;
         }
      }
   }
   .explore-charts-section {
      padding-top: 60px;
   }
   .build-charts-maps,
   .dashboard-simple-column {
      padding-top: 35px;
   }
   .get-started-section {
      padding-top: 15px;
   }
}
</style>
